import { DEEP_CONFIG, SELECT, SHALLOW_CONFIG, SIZE_WIDTH_LEFT_85, SIZE_WIDTH_LEFT_88, SIZE_WIDTH_RIGHT_85, SIZE_WIDTH_RIGHT_88 } from '../../utils';
import { findClosest, WIDTHS_35_CM, WIDTHS_58_CM } from './WallWidthUtils';
/**
 * Get the correct selectIndex from the options in the cmsState.
 *
 * @param cmsStateForm
 * @param sliderId
 * @param value The selected value.
 * @returns The index.
 */
export const getCmsSelectIndex = (cmsStateForm, sliderId, value) => {
    return (cmsStateForm
        .find(item => item.id === sliderId)
        ?.options.findIndex(o => o.value === value.toString()) ?? 0);
};
/**
 * Select a value in the slider that is currently hidden, which is the closest available to the value of the visible slider.
 *
 * If 110cm is chosen in the SIZE_WIDTH_LEFT_85 slider, 113cm will be selected in
 * the SIZE_WIDTH_LEFT_88 slider and vice versa. This makes switching between the sliders feel less choppy.
 *
 * @param formState The form state.
 * @param cmsStateForm The cms form state.
 * @param hiddenSliderId The id of the currently hidden slider.
 * @param visibleSliderValue The current value for the visible slider.
 * @param widths The widths to select a value from. Will be opposite to the list of values for the visible slider.
 * @param selectOption Function for selecting an option.
 */
const handleHiddenSlider = (formState, cmsStateForm, hiddenSliderId, visibleSliderValue, widths, selectOption) => {
    const closest = findClosest(widths, visibleSliderValue);
    // select closest on hidden slider
    if (formState[hiddenSliderId] !== closest.toString()) {
        const selectIndex = getCmsSelectIndex(cmsStateForm, hiddenSliderId, closest);
        selectOption(hiddenSliderId, closest.toString(), SELECT, selectIndex);
    }
};
/**
 * Determine if a form element in the corner guide should be hidden.
 * We have two sets of inputs with different values depending on what configuration (depth) is selected.
 *
 * @param id Id of the form element (slider/select).
 * @param formState ???.
 * @returns If the element should be hidden.
 */
export const isHideCornerInputElement = (id, formState) => {
    if (id === SIZE_WIDTH_RIGHT_88 && formState?.selectState?.size_depth_left?.selectedIndex === 1) {
        return true;
    }
    if (id === SIZE_WIDTH_LEFT_88 && formState?.selectState?.size_depth_right?.selectedIndex === 1) {
        return true;
    }
    if (id === SIZE_WIDTH_RIGHT_85 && formState?.selectState?.size_depth_left?.selectedIndex === 0) {
        return true;
    }
    if (id === SIZE_WIDTH_LEFT_85 && formState?.selectState?.size_depth_right?.selectedIndex === 0) {
        return true;
    }
};
/**
 * Make sure that the selections in the formState is allowed, otherwise change it.
 * There are certain combination that are disallowed.
 *
 * @param formState The form state.
 * @param cmsState The cms state form.
 * @param wallOneValues The values allowed for the main wall.
 * @param wallTwoValues The values allowed for the adjacent wall.
 * @param selectOption Function for selecting an option for a given input.
 */
export const handleInputCombinations = (formState, cmsState, wallOneValues, wallTwoValues, selectOption) => {
    if (formState.size_depth_left === DEEP_CONFIG) {
        const isAllowed = wallTwoValues.includes(Number(formState.size_width_right_85));
        if (!isAllowed) {
            const widthRightMillimeter = Number(formState.size_width_right_85);
            const closest = findClosest(wallTwoValues, widthRightMillimeter);
            const selectIndex = getCmsSelectIndex(cmsState.form, SIZE_WIDTH_RIGHT_85, closest);
            selectOption('size_width_right_85', closest.toString(), SELECT, selectIndex);
        }
        handleHiddenSlider(formState, cmsState.form, SIZE_WIDTH_RIGHT_88, Number(formState.size_width_right_85), WIDTHS_35_CM, selectOption);
    }
    if (formState.size_depth_right === DEEP_CONFIG) {
        const isAllowed = wallOneValues.includes(Number(formState.size_width_left_85));
        if (!isAllowed) {
            const widthLeftMillimeter = Number(formState.size_width_left_85);
            const closest = findClosest(wallOneValues, widthLeftMillimeter);
            const selectIndex = getCmsSelectIndex(cmsState.form, SIZE_WIDTH_LEFT_85, closest);
            selectOption('size_width_left_85', closest.toString(), SELECT, selectIndex);
        }
        handleHiddenSlider(formState, cmsState.form, SIZE_WIDTH_LEFT_88, Number(formState.size_width_left_85), WIDTHS_35_CM, selectOption);
    }
    if (formState.size_depth_left === SHALLOW_CONFIG) {
        const isAllowed = wallTwoValues.includes(Number(formState.size_width_right_88));
        if (!isAllowed) {
            const widthRightMillimeter = Number(formState.size_width_right_88);
            const closest = findClosest(wallTwoValues, widthRightMillimeter);
            const selectIndex = getCmsSelectIndex(cmsState.form, SIZE_WIDTH_RIGHT_88, closest);
            selectOption('size_width_right_88', closest.toString(), SELECT, selectIndex);
        }
        handleHiddenSlider(formState, cmsState.form, SIZE_WIDTH_RIGHT_85, Number(formState.size_width_right_88), WIDTHS_58_CM, selectOption);
    }
    if (formState.size_depth_right === SHALLOW_CONFIG) {
        const isAllowed = wallOneValues.includes(Number(formState.size_width_left_88));
        if (!isAllowed) {
            const widthLeftMillimeter = Number(formState.size_width_left_88);
            const closest = findClosest(wallOneValues, widthLeftMillimeter);
            const selectIndex = getCmsSelectIndex(cmsState.form, SIZE_WIDTH_LEFT_88, closest);
            selectOption('size_width_left_88', closest.toString(), SELECT, selectIndex);
        }
        handleHiddenSlider(formState, cmsState.form, SIZE_WIDTH_LEFT_85, Number(formState.size_width_left_88), WIDTHS_58_CM, selectOption);
    }
};
/**
 * Get the width in mm for left and right sections based on the depth chosen.
 *
 * @param formState The formState.
 * @returns Widths of the left and right section in millimeters.
 */
export const getWidths = (formState) => {
    const leftWidth = formState.size_depth_right === DEEP_CONFIG
        ? Number(formState.size_width_left_85)
        : Number(formState.size_width_left_88);
    const rightWidth = formState.size_depth_left === DEEP_CONFIG
        ? Number(formState.size_width_right_85)
        : Number(formState.size_width_right_88);
    return { leftWidth, rightWidth };
};
/**
 * Get width data from the CMS form and update the formState accordingly.
 *
 * @param formState
 * @param cmsStateForm
 * @param selectOption
 */
export const setWidthDataFromCmsData = (formState, cmsStateForm, selectOption) => {
    // Get form data from the cmsState
    const widthLeft85 = cmsStateForm.find(item => item.id === SIZE_WIDTH_LEFT_85)?.value?.toString();
    const widthRight85 = cmsStateForm
        .find(item => item.id === SIZE_WIDTH_RIGHT_85)
        ?.value?.toString();
    const widthLeft88 = cmsStateForm.find(item => item.id === SIZE_WIDTH_LEFT_88)?.value?.toString();
    const widthRight88 = cmsStateForm
        .find(item => item.id === SIZE_WIDTH_RIGHT_88)
        ?.value?.toString();
    if (!formState[SIZE_WIDTH_LEFT_85] && widthLeft85) {
        const index = cmsStateForm
            .find(item => item.id === SIZE_WIDTH_LEFT_85)
            ?.options.findIndex(option => option.value === widthLeft85);
        selectOption(SIZE_WIDTH_LEFT_85, widthLeft85, SELECT, index);
    }
    if (!formState[SIZE_WIDTH_RIGHT_85] && widthRight85) {
        const index = cmsStateForm
            .find(item => item.id === SIZE_WIDTH_RIGHT_85)
            ?.options.findIndex(option => option.value === widthRight85);
        selectOption(SIZE_WIDTH_RIGHT_85, widthRight85, SELECT, index);
    }
    if (!formState[SIZE_WIDTH_LEFT_88] && widthLeft88) {
        const index = cmsStateForm
            .find(item => item.id === SIZE_WIDTH_LEFT_88)
            ?.options.findIndex(option => option.value === widthLeft88);
        selectOption(SIZE_WIDTH_LEFT_88, widthLeft88, SELECT, index);
    }
    if (!formState[SIZE_WIDTH_RIGHT_88] && widthRight88) {
        const index = cmsStateForm
            .find(item => item.id === SIZE_WIDTH_RIGHT_88)
            ?.options.findIndex(option => option.value === widthRight88);
        selectOption(SIZE_WIDTH_RIGHT_88, widthRight88, SELECT, index);
    }
};
//# sourceMappingURL=FilterUtils.js.map